import React from 'react';
import { Award, GraduationCap, Briefcase, Mail, Linkedin } from 'lucide-react';
import './About.css';

const About = () => {
  const education = [
    {
      degree: "Ph.D. in Electrical & Computer Engineering (AI Specialization)",
      institution: "Georgia Institute of Technology",
      year: "2020 – 2025",
      advisor: "Prof. Abhijit Chatterjee",
      thesis: "Neuro-Adaptive Systems for Efficient Temporal Learning and Dynamical Control",
      honors: "Sigma Xi Best Ph.D. Thesis Award Winner (2026)"
    },
    {
      degree: "Bachelor of Technology in Electrical Engineering",
      institution: "Indian Institute of Technology (IIT)",
      year: "2016 – 2020",
      honors: "First Class with Distinction"
    }
  ];

  const experience = [
    {
      role: "AI Scientist / Quantitative Researcher",
      organization: "Mercuria Energy America",
      period: "May 2025 – Present",
      location: "Houston, TX",
      description: "Developing production AI architectures and spatio-temporal forecasting models for power grids and natural gas physical markets."
    },
    {
      role: "Graduate Research Assistant",
      organization: "Georgia Institute of Technology",
      period: "August 2020 – May 2025",
      location: "Atlanta, GA",
      description: "Researched efficient neural architectures, spiking networks, state-space representations, and dynamical systems control."
    }
  ];

  const awards = [
    {
      title: "Georgia Tech Sigma Xi Best Ph.D. Thesis Award",
      year: "2026",
      issuer: "Sigma Xi Scientific Research Honor Society",
      desc: "Recognizing outstanding Ph.D. thesis research across Georgia Tech."
    },
    {
      title: "NeurIPS 2025 Accepted Paper (FLAME)",
      year: "2025",
      issuer: "Neural Information Processing Systems",
      desc: "Fast Long-context Adaptive Memory for Event-based Vision."
    },
    {
      title: "ICML 2025 Accepted Paper",
      year: "2025",
      issuer: "International Conference on Machine Learning",
      desc: "Dynamical Systems-Inspired Pruning Strategy for Graph Attention Networks."
    },
    {
      title: "MLCommons Rising Stars Award",
      year: "2024",
      issuer: "MLCommons Organization",
      desc: "Awarded to top emerging researchers in machine learning systems."
    },
    {
      title: "Outstanding ECE Graduate Research Assistant Award",
      year: "2024",
      issuer: "Georgia Institute of Technology",
      desc: "Highest honor awarded to GRA researchers in ECE."
    }
  ];

  return (
    <div className="about-page">
      {/* Bio Banner */}
      <header className="about-header glass">
        <div className="about-profile-wrap">
          <img src="profile.png" alt="Biswadeep Chakraborty" className="about-avatar" />
          <div className="about-bio-text">
            <span className="about-kicker">Biographical Overview</span>
            <h1>Biswadeep Chakraborty, Ph.D.</h1>
            <p className="about-title">
              AI Scientist at Mercuria Energy America • Ph.D. in AI from Georgia Tech
            </p>
            <p className="about-narrative">
              My research lies at the intersection of <strong>Artificial Intelligence, Energy Systems, and Physical Critical Infrastructure</strong>. 
              Having completed my Ph.D. at Georgia Institute of Technology, my doctoral work developed foundational theory and efficient architectures for temporal learning, spiking neural networks, state-space models, and dynamical systems control.
            </p>
            <p className="about-narrative">
              Today, as an AI Scientist at Mercuria Energy, I bridge fundamental machine learning with real-world infrastructure optimization—building high-performance predictive systems for power grids, natural gas markets, and regional energy delivery networks.
            </p>

            <div className="about-socials">
              <a href="mailto:biswadeep@gatech.edu" className="social-btn">
                <Mail className="btn-icon" /> Email
              </a>
              <a href="https://www.linkedin.com/in/biswadeepc/" target="_blank" rel="noopener noreferrer" className="social-btn">
                <Linkedin className="btn-icon" /> LinkedIn
              </a>
              <a href="https://scholar.google.com/citations?user=8soIjY8AAAAJ&hl=en&authuser=1" target="_blank" rel="noopener noreferrer" className="social-btn">
                <Award className="btn-icon" /> Google Scholar
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Career Experience & Education */}
      <section className="about-split-grid">
        {/* Education Card */}
        <div className="split-card glass">
          <div className="card-head">
            <GraduationCap className="head-icon" />
            <h2>Education</h2>
          </div>
          <div className="timeline-list">
            {education.map((item, idx) => (
              <div key={idx} className="timeline-item">
                <span className="timeline-year">{item.year}</span>
                <h3 className="timeline-title">{item.degree}</h3>
                <span className="timeline-inst">{item.institution}</span>
                {item.advisor && <p className="timeline-detail"><strong>Advisor:</strong> {item.advisor}</p>}
                {item.thesis && <p className="timeline-detail"><strong>Thesis:</strong> {item.thesis}</p>}
                {item.honors && <p className="timeline-honor"><Award className="honor-icon" /> {item.honors}</p>}
              </div>
            ))}
          </div>
        </div>

        {/* Experience Card */}
        <div className="split-card glass">
          <div className="card-head">
            <Briefcase className="head-icon" />
            <h2>Professional Positions</h2>
          </div>
          <div className="timeline-list">
            {experience.map((item, idx) => (
              <div key={idx} className="timeline-item">
                <span className="timeline-year">{item.period}</span>
                <h3 className="timeline-title">{item.role}</h3>
                <span className="timeline-inst">{item.organization} • {item.location}</span>
                <p className="timeline-detail">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Selected Awards & Recognition */}
      <section className="awards-section glass">
        <div className="card-head">
          <Award className="head-icon" />
          <h2>Selected Scientific Awards & Recognition</h2>
        </div>
        <div className="awards-grid">
          {awards.map((award, idx) => (
            <div key={idx} className="award-card glass-hover">
              <span className="award-year">{award.year}</span>
              <h3>{award.title}</h3>
              <span className="award-issuer">{award.issuer}</span>
              <p>{award.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default About;
